"use client";

import { FormEvent, useState } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import {
  signInWithEmailAndPassword,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithPopup,
} from "firebase/auth";
import { getAuth } from "@/lib/firebaseClient";
import { logLoginEvent } from "@/lib/api/access";
import TurnstileWidget from "@/components/TurnstileWidget";
import { verifyTurnstile } from "@/lib/turnstile";

const EyeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
    <circle cx="12" cy="12" r="3"/>
  </svg>
);

const EyeOffIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
    <line x1="1" y1="1" x2="23" y2="23"/>
  </svg>
);

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
    <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
    <path d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
    <path d="M3.964 10.707A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.707V4.961H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.039l3.007-2.332z" fill="#FBBC05"/>
    <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.96L3.964 7.293C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
  </svg>
);

const AppleIcon = () => (
  <svg width="17" height="18" viewBox="0 0 814 1000" xmlns="http://www.w3.org/2000/svg" fill="currentColor">
    <path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76 0-103.7 40.8-165.9 40.8s-105-38.8-155.5-127.4C46 790.7 0 663 0 541.8c0-207.5 135.4-317.3 269-317.3 71 0 130.5 46.4 175 46.4 42.3 0 109.3-49 192.3-49 31 0 113.3 2.6 168.2 79zm-225.1-234.7c31.6-37.4 54.3-89.2 54.3-140.9 0-7.1-.6-14.3-1.9-20.1-51.5 2-112.5 34.5-148.5 75.5-28.5 32.4-55.1 83.6-55.1 136 0 7.8 1.3 15.6 1.9 18.1 3.2.6 8.4 1.3 13.6 1.3 46.5 0 102.5-30.4 135.7-69.9z"/>
  </svg>
);

const PLATFORM_OWNER_EMAIL = "peregrina.dt@gmail.com";

export default function SignInPage() {
  const router = useRouter();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tsToken, setTsToken] = useState<string | null>(null);
  const [tsKey, setTsKey] = useState(0); // bump para remontar el widget (token es de un solo uso)

  const getDestination = (userEmail: string | null) =>
    userEmail === PLATFORM_OWNER_EMAIL ? "/owner/dashboard" : "/dashboard";

  const handleSocialSignIn = async (provider: GoogleAuthProvider | OAuthProvider) => {
    const auth = getAuth();
    if (!auth) return;
    setLoading(true);
    setError(null);
    try {
      const { user } = await signInWithPopup(auth, provider);
      // Forzar refresh del token para obtener custom claims actualizados
      const tokenResult = await user.getIdTokenResult(true);
      const breweryId = (tokenResult.claims as any)?.breweryId;

      // Usuario autenticado pero sin cervecería → mandar al registro
      if (!breweryId && user.email !== PLATFORM_OWNER_EMAIL) {
        router.push("/signup");
        return;
      }

      // Registro de accesos (best-effort, no bloquea el ingreso).
      void logLoginEvent(tokenResult.signInProvider ?? "oauth");

      router.push(getDestination(user.email));
    } catch (err: any) {
      if (err?.code !== "auth/popup-closed-by-user" && err?.code !== "auth/cancelled-popup-request") {
        setError("No se pudo iniciar sesión. Intentá de nuevo.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = () => {
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: "select_account" });
    handleSocialSignIn(provider);
  };

  const handleAppleSignIn = () => {
    const provider = new OAuthProvider("apple.com");
    provider.addScope("email");
    provider.addScope("name");
    provider.setCustomParameters({ locale: "es" });
    handleSocialSignIn(provider);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const auth = getAuth();
    if (!auth) {
      setError("Firebase no disponible.");
      setLoading(false);
      return;
    }

    // Verificación anti-bot (Turnstile)
    if (!tsToken) {
      setError("Completá la verificación de seguridad.");
      setLoading(false);
      return;
    }
    const human = await verifyTurnstile(tsToken);
    if (!human) {
      setError("La verificación de seguridad falló. Probá de nuevo.");
      setTsToken(null);
      setTsKey((k) => k + 1);
      setLoading(false);
      return;
    }

    try {
      const { user } = await signInWithEmailAndPassword(auth, email, password);
      await user.getIdToken(true);
      // Registro de accesos (best-effort, no bloquea el ingreso).
      void logLoginEvent("password");
      router.push(getDestination(user.email));
    } catch (err: any) {
      console.error(err);
      // El token Turnstile es de un solo uso: reiniciamos el widget para reintentar.
      setTsToken(null);
      setTsKey((k) => k + 1);
      const code = err?.code as string | undefined;
      if (code === "auth/invalid-credential" || code === "auth/user-not-found" || code === "auth/wrong-password") {
        setError("Email o contraseña incorrectos. Revisá los datos o usá «Olvidé mi contraseña».");
      } else if (code === "auth/invalid-email") {
        setError("El email no es válido.");
      } else {
        setError(err?.message ?? "Error al iniciar sesión.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4 py-6">
      <div className="fixed top-4 left-4">
        <a
          href="/"
          className="inline-flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-700 border border-gray-200 hover:border-gray-400 bg-white rounded-lg px-3 py-1.5 transition-colors"
        >
          ← Inicio
        </a>
      </div>

      <div className="max-w-md w-full">
        <div className="text-center mb-5">
          <div className="text-4xl mb-1">🍺</div>
          <h1 className="text-2xl font-bold text-gray-900">Cuaderno Cervecero</h1>
          <p className="text-gray-500 mt-1 text-sm">Iniciá sesión para acceder a tu panel</p>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Iniciar sesión</h2>

          {/* Botones sociales */}
          <div className="flex flex-col gap-2.5 mb-4">
            <button
              type="button"
              onClick={handleGoogleSignIn}
              disabled={loading}
              className="w-full flex items-center justify-center gap-3 border border-gray-300 rounded-lg px-4 py-2.5 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 active:bg-gray-100 transition-colors disabled:opacity-60"
            >
              <GoogleIcon />
              Continuar con Google
            </button>

            <button
              type="button"
              onClick={handleAppleSignIn}
              disabled={loading}
              className="w-full flex items-center justify-center gap-3 border border-gray-300 rounded-lg px-4 py-2.5 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 active:bg-gray-100 transition-colors disabled:opacity-60"
            >
              <AppleIcon />
              Continuar con Apple
            </button>
          </div>

          <div className="flex items-center gap-3 mb-4">
            <div className="flex-1 h-px bg-gray-200" />
            <span className="text-xs text-gray-400">o con email</span>
            <div className="flex-1 h-px bg-gray-200" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                name="email"
                required
                autoFocus
                autoComplete="email"
                placeholder="hola@micerveceria.com"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Contraseña</label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  required
                  minLength={6}
                  autoComplete="current-password"
                  placeholder="Tu contraseña"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                  aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                >
                  {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                </button>
              </div>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            <div className="flex justify-center pt-1">
              <TurnstileWidget
                key={tsKey}
                onToken={setTsToken}
                onExpire={() => setTsToken(null)}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-amber-500 hover:bg-amber-600 active:bg-amber-700 text-white py-2.5 rounded-lg font-semibold text-sm transition-colors disabled:opacity-60 disabled:cursor-not-allowed mt-2"
            >
              {loading ? "Ingresando…" : "Iniciar sesión →"}
            </button>
          </form>

          <div className="mt-4 text-center">
            <button
              type="button"
              className="text-sm text-gray-400 hover:text-gray-600 underline"
              onClick={() => router.push("/forgot-password")}
            >
              Olvidé mi contraseña
            </button>
          </div>
        </div>

        <p className="text-center text-sm text-gray-500 mt-4">
          ¿No tenés cuenta?{" "}
          <Link href="/signup" className="text-amber-600 hover:text-amber-700 font-medium">
            Registrá tu cervecería gratis →
          </Link>
        </p>
      </div>
    </div>
  );
}
